import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Modal } from "@/components";
import { ButtonColor } from "@/components/Button/types";
import { ROUTE_CONFIG } from "@/routes/config";

import { DeliveryStatus, DeliveryTrigger } from "./DeliveryTable";
import { deliveryDuration, deliveryTime, deliveryWait } from "./deliveryPresentation";
import { useDeliveryDetail } from "./useDeliveryData";

import type { WebhookDelivery } from "../schema/webhookDelivery";

export function DeliveryDetailDialog({
	webhookId,
	deliveryId,
	onClose,
	onRedeliver,
	onDetail,
}: {
	webhookId: number;
	deliveryId: number;
	onClose: () => void;
	onRedeliver: (row: WebhookDelivery) => void;
	onDetail: (id: number) => void;
}) {
	const { t } = useTranslation("translation", { keyPrefix: "webhookHealth.deliveries" });
	const { data: row, isFetching, isError, refetch } = useDeliveryDetail(webhookId, deliveryId);

	return (
		<Modal
			cancelButtonText={t("close")}
			handleClose={onClose}
			handleSave={row && !isError && row.status === "GIVEN_UP" ? () => onRedeliver(row) : undefined}
			isOpened
			size="lg"
			submitButtonColor={ButtonColor.ACTION}
			submitButtonText={t("redeliver")}
			takeFocus
			title={t("delivery", { name: row?.lead?.name ?? "" })}
		>
			{isError ? (
				<div role="alert">
					{t("loadFailed")}{" "}
					<button
						className="cursor-pointer text-brand-light"
						type="button"
						onClick={() => refetch()}
					>
						{t("retry")}
					</button>
				</div>
			) : !row ? (
				<p>{t("loading")}</p>
			) : (
				<div aria-busy={isFetching} className="flex flex-col gap-4 text-sm text-text">
					<DeliveryStatus row={row} />
					<dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
						<dt>{t("deliveryId")}</dt>
						<dd className="break-all font-mono">{row.deliveryId}</dd>
						<dt>{t("trigger")}</dt>
						<dd>
							<DeliveryTrigger trigger={row.trigger} /> {row.list?.name}
						</dd>
						<dt>{t("lead")}</dt>
						<dd>
							{row.lead?.name ?? t("removed")} {row.lead?.company}{" "}
							{row.lead && row.listId && row.listPersonId && (
								<Link
									className="cursor-pointer text-brand-light"
									to={`${ROUTE_CONFIG.LISTS}/detail/${row.listId}?chatListPersonId=${row.listPersonId}`}
								>
									{t("openLead")}
								</Link>
							)}
						</dd>
						<dt>{t("created")}</dt>
						<dd>{deliveryTime(row.createdAt)}</dd>
						<dt>{t("later")}</dt>
						<dd>
							{row.redeliveries.length === 0
								? t("none")
								: row.redeliveries.map((later) => (
										<button
											key={later.id}
											className="block cursor-pointer text-brand-light"
											type="button"
											onClick={() => onDetail(later.id)}
										>
											{deliveryTime(later.createdAt)} · {t(later.status)}
										</button>
									))}
						</dd>
					</dl>
					<h3 className="font-semibold">{t("attempts")}</h3>
					{row.givenUpKind === "NOT_SENT" ? (
						<p>{t("notSent")}</p>
					) : row.status === "QUEUED" && row.attempts.length === 0 ? (
						<p>{t("queued")}</p>
					) : (
						<div className="overflow-x-auto">
							<table className="w-full text-left text-xs">
								<thead className="bg-background-light">
									<tr>
										{["#", "time", "http", "answer", "took", "wait"].map((key) => (
											<th key={key} className="p-2">
												{key === "#" ? key : t(key)}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{row.attempts.map((attempt, index) => (
										<tr key={attempt.attemptNumber} className="border-b border-border-light">
											<td className="p-2">{attempt.attemptNumber}</td>
											<td className="p-2 whitespace-nowrap">{deliveryTime(attempt.attemptedAt)}</td>
											<td className="p-2">{attempt.httpStatus ?? "–"}</td>
											<td className="max-w-64 break-words p-2">{attempt.answer ?? "–"}</td>
											<td className="p-2 whitespace-nowrap">
												{deliveryDuration(attempt.durationMs)}
											</td>
											<td className="p-2 whitespace-nowrap">
												{index === 0
													? "–"
													: deliveryWait(
															row.attempts[index - 1].attemptedAt,
															attempt.attemptedAt,
															t
														)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
					<h3 className="font-semibold">{t("lastResponse")}</h3>
					<pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-background-light p-3 text-xs">
						{row.lastResponseExcerpt ?? "–"}
					</pre>
					{row.lastError && (
						<div>
							<h3 className="font-semibold">{t("error")}</h3>
							<p className="text-text">{row.lastError}</p>
						</div>
					)}
					<p className="text-xs text-text-meta">{t("payload")}</p>
				</div>
			)}
		</Modal>
	);
}
